import { useState } from "react";
import { Book, Copy, Terminal, Package, Webhook, Rocket, Key, ChevronRight, ShieldCheck, Wand2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

// CF-1 · Merchant Developer Docs — Cyannect supports TWO first-class
// redemption models. The merchant picks per offer:
//   • Automatic Discount (cy_ref, code-free)
//   • Coupon Code (shopper enters a code)
// Both are actively supported. Neither is "legacy".

const SECTIONS = [
  { id: "start", label: "Quick start", icon: Rocket, group: "primary" },
  { id: "how-it-works", label: "How it works", icon: Book, group: "primary" },
  { id: "auth", label: "Authentication", icon: Key, group: "primary" },
  { id: "sdk", label: "Install the SDK", icon: Package, group: "primary" },
  { id: "validate", label: "Auto-apply the discount", icon: Wand2, group: "primary" },
  { id: "checkout", label: "Report checkout", icon: Terminal, group: "primary" },
  { id: "payment-attempt", label: "Report payment attempt", icon: Terminal, group: "primary" },
  { id: "payment-failed", label: "Report payment failure", icon: Terminal, group: "primary" },
  { id: "conversion", label: "Report the conversion", icon: ShieldCheck, group: "primary" },
  { id: "refund", label: "Refunds & cancellations", icon: RotateCcw, group: "primary" },
  { id: "webhooks", label: "Webhooks", icon: Webhook, group: "primary" },
  { id: "legacy", label: "Coupon Code Integration", icon: Book, group: "coupon" },
];

function Code({ children, lang = "bash", testid }) {
  return (
    <div className="relative group" data-testid={testid}>
      <pre className="font-mono text-xs bg-slate-900 text-cyan-100 rounded-2xl p-4 overflow-x-auto border border-slate-800">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">{lang}</div>
        {children}
      </pre>
      <button
        onClick={() => { navigator.clipboard.writeText(String(children).trim()); toast.success("Copied"); }}
        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition text-xs bg-slate-800 hover:bg-slate-700 text-white px-2 py-1 rounded-md flex items-center gap-1"
      >
        <Copy className="w-3 h-3" /> Copy
      </button>
    </div>
  );
}

function Section({ id, title, subtitle, badge, children }) {
  return (
    <section id={id} className="pf-card p-6 scroll-mt-24" data-testid={`docs-section-${id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-2xl text-slate-900">{title}</h3>
          {subtitle ? <p className="text-sm text-slate-500 mt-1">{subtitle}</p> : null}
        </div>
        {badge ? <span className="pf-chip pf-chip-cyan text-[10px] uppercase tracking-widest">{badge}</span> : null}
      </div>
      <div className="mt-4 space-y-4 text-sm text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

export default function MerchantDocs() {
  const [active, setActive] = useState("start");
  const scrollTo = (id) => {
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const primary = SECTIONS.filter((s) => s.group === "primary");
  const legacy = SECTIONS.filter((s) => s.group === "coupon");
  const originForCode = typeof window !== "undefined" ? window.location.origin : "https://cyannect.com";

  return (
    <div data-testid="merchant-docs" className="grid grid-cols-1 md:grid-cols-[240px_minmax(0,1fr)] gap-8">
      <aside className="md:sticky md:top-24 h-fit pf-card p-4">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold px-2">Automatic Discount</div>
        <nav className="mt-3 space-y-1">
          {primary.map((s) => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className={`w-full text-left px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition ${active === s.id ? "pf-btn-primary" : "text-slate-600 hover:bg-white/60"}`}
              data-testid={`docs-nav-${s.id}`}
            >
              <s.icon className="w-4 h-4" />
              <span className="truncate">{s.label}</span>
              <ChevronRight className="w-3 h-3 ml-auto opacity-40 shrink-0" />
            </button>
          ))}
        </nav>

        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold px-2 mt-6">Coupon Code</div>
        <nav className="mt-3 space-y-1">
          {legacy.map((s) => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className={`w-full text-left px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition ${active === s.id ? "pf-btn-primary" : "text-slate-500 hover:bg-white/60"}`}
              data-testid={`docs-nav-${s.id}`}
            >
              <s.icon className="w-4 h-4" />
              <span className="truncate">{s.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      <div>
        <div className="pf-chip pf-chip-cyan" data-testid="docs-primary-chip">Automatic Discount · Best shopper experience</div>
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Integrate Cyannect</h2>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl" data-testid="docs-lead">
          Cyannect supports two first-class redemption models — pick per offer. <strong>Automatic Discount</strong> is a code-free, cy_ref-based flow that requires a one-time checkout integration. <strong>Coupon Code</strong> is a simpler flow where the shopper enters a code your checkout validates with Cyannect. Both feed the same verified-conversion, cashback and analytics pipeline.
        </p>

        {/* CF-1 · Dual-integration comparison table */}
        <div className="pf-card mt-6 overflow-hidden" data-testid="docs-integration-compare">
          <div className="grid grid-cols-3 text-xs [overflow-wrap:anywhere]">
            <div className="p-3 bg-slate-100 font-semibold uppercase tracking-widest text-slate-600">Feature</div>
            <div className="p-3 bg-cyan-50 font-semibold uppercase tracking-widest text-cyan-800">Automatic Discount</div>
            <div className="p-3 bg-amber-50 font-semibold uppercase tracking-widest text-amber-800">Coupon Code</div>

            <div className="p-3 border-t border-slate-200 text-slate-600">Shopper enters code</div>
            <div className="p-3 border-t border-slate-200 text-slate-900">No</div>
            <div className="p-3 border-t border-slate-200 text-slate-900">Yes</div>

            <div className="p-3 border-t border-slate-200 text-slate-600">Merchant integration effort</div>
            <div className="p-3 border-t border-slate-200 text-slate-900">One-time checkout integration (SDK + <code className="font-mono text-cyan-700">/offers/validate</code>)</div>
            <div className="p-3 border-t border-slate-200 text-slate-900">Simple <code className="font-mono text-amber-700">/coupons/validate</code> call in checkout</div>

            <div className="p-3 border-t border-slate-200 text-slate-600">Attribution mechanism</div>
            <div className="p-3 border-t border-slate-200 text-slate-900"><code className="font-mono text-cyan-700">cy_ref</code> first-party cookie</div>
            <div className="p-3 border-t border-slate-200 text-slate-900">Coupon code lookup</div>

            <div className="p-3 border-t border-slate-200 text-slate-600">Product-level eligibility</div>
            <div className="p-3 border-t border-slate-200 text-slate-900">Yes (SKU / product_id)</div>
            <div className="p-3 border-t border-slate-200 text-slate-900">Yes (per campaign rules)</div>

            <div className="p-3 border-t border-slate-200 text-slate-600">Conversion reporting</div>
            <div className="p-3 border-t border-slate-200 text-slate-900"><code className="font-mono text-cyan-700">POST /api/v1/conversions</code></div>
            <div className="p-3 border-t border-slate-200 text-slate-900"><code className="font-mono text-amber-700 break-all">POST /api/v1/coupons/redeem</code></div>

            <div className="p-3 border-t border-slate-200 text-slate-600">Cashback support</div>
            <div className="p-3 border-t border-slate-200 text-slate-900">Yes</div>
            <div className="p-3 border-t border-slate-200 text-slate-900">Yes</div>

            <div className="p-3 border-t border-slate-200 text-slate-600">Best suited for</div>
            <div className="p-3 border-t border-slate-200 text-slate-900">Merchants with engineering capacity who want the smoothest UX</div>
            <div className="p-3 border-t border-slate-200 text-slate-900">Merchants who want to launch offers quickly with minimal engineering</div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
          <div className="pf-card p-4"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Base URL</div><div className="font-mono text-sm text-slate-900 mt-1 truncate">{originForCode}/api</div></div>
          <div className="pf-card p-4"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Auth header</div><div className="font-mono text-sm text-slate-900 mt-1 break-all">X-Api-Key: cyk_live_…</div></div>
          <div className="pf-card p-4"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Response format</div><div className="font-mono text-sm text-slate-900 mt-1 break-all">application/json</div></div>
        </div>

        <div className="mt-8 space-y-6">
          <Section id="start" title="Quick start" subtitle="Five steps to a working Automatic Discount integration." badge="Automatic Discount">
            <ol className="list-decimal pl-5 space-y-2">
              <li>Complete <a href="/merchant/verification" className="text-cyan-700 underline">business verification</a>.</li>
              <li>Create your first <a href="/merchant/campaigns" className="text-cyan-700 underline">offer</a> and pick <strong>Automatic Discount</strong> as the redemption method. (Pick <strong>Coupon Code</strong> instead for the simpler code-based flow.)</li>
              <li>Add the <code className="font-mono text-cyan-700">cyannect.js</code> SDK to every page of your site so <code className="font-mono text-cyan-700">cy_ref</code> is captured as a first-party cookie.</li>
              <li>In your cart engine, call <code className="font-mono text-cyan-700">POST /api/v1/offers/validate</code> from your backend and apply the returned discount.</li>
              <li>After payment succeeds on your side, call <code className="font-mono text-cyan-700">POST /api/v1/conversions</code> from your server with an <code className="font-mono text-cyan-700">Idempotency-Key</code>.</li>
            </ol>
            <p className="text-slate-500 text-xs">You never ask the shopper to enter or paste a code.</p>
          </Section>

          <Section id="how-it-works" title="How it works">
            <ol className="list-decimal pl-5 space-y-2">
              <li>Shopper taps <b>Shop offer</b> in Cyannect. Cyannect mints a unique referral (<code className="font-mono text-cyan-700">CY-XXXXXXXX</code>) bound to (shopper × campaign).</li>
              <li>Shopper is opened in the OS browser at your merchant URL with <code className="font-mono text-cyan-700">?cy_ref=CY-XXXXXXXX</code>.</li>
              <li><code className="font-mono text-cyan-700">cyannect.js</code> stores the token in a first-party cookie and cleans the URL.</li>
              <li>Shopper adds an eligible product to cart. Your cart backend calls <code className="font-mono text-cyan-700">/offers/validate</code>. Cyannect returns the eligible discount.</li>
              <li>Your cart applies the discount. Shopper sees a normal discounted total — no code field involved.</li>
              <li>Shopper clicks Pay. Payment succeeds. Your server calls <code className="font-mono text-cyan-700">/conversions</code>. Cyannect records the verified conversion + fires <code className="font-mono text-cyan-700">CONVERSION_RECEIVED</code>.</li>
            </ol>
          </Section>

          <Section id="auth" title="Authentication">
            <p>Every server-side call uses an <b>API key</b> tied to your store. Send it as <code className="font-mono text-cyan-700">X-Api-Key</code> or <code className="font-mono text-cyan-700">Authorization: Bearer</code>. Rotate keys at <a href="/merchant/api-keys" className="text-cyan-700 underline">/merchant/api-keys</a>.</p>
            <Code lang="header" testid="docs-auth-code">{`X-Api-Key: cyk_live_XXXXXXXXXXXXXXXXXXXXXXXX
Content-Type: application/json`}</Code>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900" data-testid="docs-auth-warning">
              <b>Never expose your API key in browser JavaScript, mobile clients or public HTML.</b> The Cyannect SDK for browsers deliberately cannot report conversions — the key stays on your backend at all times. Cross-merchant calls are rejected with <code className="font-mono">403</code>.
            </div>
          </Section>

          <Section id="sdk" title="Install the SDK">
            <p>One <code className="font-mono text-cyan-700">&lt;script&gt;</code> tag on every page of your site. On load the SDK reads <code className="font-mono text-cyan-700">?cy_ref=</code> from the URL, drops it in a first-party cookie for 30 days, then strips the parameter so it isn't shared or bookmarked.</p>
            <Code lang="html" testid="docs-sdk-install">{`<script src="${originForCode}/sdk/v1/cyannect.js"></script>
<script>
  Cyannect.init({ merchantId: "YOUR_STORE_ID" });
</script>`}</Code>
            <p>The SDK exposes browser helpers for analytics — they are OPTIONAL and never handle money:</p>
            <Code lang="javascript" testid="docs-sdk-events">{`Cyannect.productViewed({ sku: "RUN-SHOE-123" });
Cyannect.addToCart({ sku: "RUN-SHOE-123", cartValue: 128.00 });
Cyannect.checkoutStarted({ orderId: "cart_abc", cartValue: 128.00 });`}</Code>
          </Section>

          <Section id="validate" title="Auto-apply the discount" subtitle="POST /api/v1/offers/validate — call from your backend when the shopper adds a product to their cart.">
            <p>Given a <code className="font-mono text-cyan-700">cy_ref</code> and the current cart items, Cyannect answers <b>eligible</b>, filters to the qualifying SKUs, and returns an upper-bound discount. <b>Your cart engine remains authoritative for the final applied discount.</b></p>
            <Code lang="curl" testid="docs-validate-curl">{`curl -X POST $CY/api/v1/offers/validate \\
  -H "Authorization: Bearer $KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "cy_ref": "CY-AR8RWFDR",
    "currency": "CAD",
    "items": [
      { "sku": "RUN-SHOE-123", "quantity": 1, "unit_price": 120.00 },
      { "sku": "JACKET-555",   "quantity": 1, "unit_price": 78.00 }
    ]
  }'`}</Code>
            <Code lang="200 OK">{`{
  "eligible": true,
  "cy_ref": "CY-AR8RWFDR",
  "offer_id": "camp_abc",
  "discount_type": "PERCENT",
  "discount_value": 20,
  "eligible_items": [
    { "sku": "RUN-SHOE-123", "quantity": 1 }
  ],
  "eligible_subtotal_minor": 12000,
  "maximum_discount_minor": 2400,
  "maximum_discount_display": "$24.00",
  "currency": "CAD"
}`}</Code>
            <p className="text-slate-500 text-xs">If <code className="font-mono text-cyan-700">eligible</code> is <code className="font-mono">false</code>, the response includes a bucketed <code className="font-mono text-cyan-700">reason</code> (<code>unknown_cy_ref</code>, <code>wrong_merchant</code>, <code>expired</code>, <code>offer_inactive</code>, <code>no_eligible_items</code>). Simply skip the discount and continue.</p>
          </Section>

          <Section id="checkout" title="Report checkout" subtitle="POST /api/v1/checkout/started — server-to-server (idempotent).">
            <p>Fire this when your checkout page loads. Analytics only — never triggers cashback or fees. Requires <code className="font-mono text-cyan-700">Idempotency-Key</code>; retries never inflate the funnel.</p>
            <Code lang="curl" testid="docs-checkout-curl">{`curl -X POST $CY/api/v1/checkout/started \\
  -H "Authorization: Bearer $KEY" \\
  -H "Idempotency-Key: cart_abc_checkout" \\
  -H "Content-Type: application/json" \\
  -d '{
    "cy_ref": "CY-AR8RWFDR",
    "checkout_id": "cart_abc",
    "currency": "CAD",
    "cart_value": 120.00,
    "items": [{ "sku": "RUN-SHOE-123", "quantity": 1 }]
  }'`}</Code>
          </Section>

          <Section id="payment-attempt" title="Report payment attempt" subtitle="POST /api/v1/payment/attempted — fired when the shopper clicks Pay Now.">
            <p>This event means <b>Pay clicked</b>, not <b>paid</b>. Analytics only — no cashback, no fees, no verified conversion.</p>
            <Code lang="curl" testid="docs-pay-curl">{`curl -X POST $CY/api/v1/payment/attempted \\
  -H "Authorization: Bearer $KEY" \\
  -H "Idempotency-Key: cart_abc_pay_attempt_1" \\
  -H "Content-Type: application/json" \\
  -d '{
    "cy_ref": "CY-AR8RWFDR",
    "checkout_id": "cart_abc",
    "currency": "CAD",
    "cart_value": 96.00
  }'`}</Code>
          </Section>

          <Section id="payment-failed" title="Report payment failure" subtitle="POST /api/v1/payment/failed — fired when a payment attempt does NOT succeed.">
            <p>Use one of the following bucketed <code className="font-mono text-cyan-700">reason</code> values (free-form text is rejected): <code className="font-mono">declined</code>, <code className="font-mono">authentication_failed</code>, <code className="font-mono">insufficient_funds</code>, <code className="font-mono">expired_card</code>, <code className="font-mono">processor_error</code>, <code className="font-mono">network_error</code>, <code className="font-mono">cancelled_by_shopper</code>, <code className="font-mono">other</code>. Never send card numbers, CVV or bank credentials.</p>
            <Code lang="curl">{`curl -X POST $CY/api/v1/payment/failed \\
  -H "Authorization: Bearer $KEY" \\
  -H "Idempotency-Key: cart_abc_pay_attempt_1_fail" \\
  -H "Content-Type: application/json" \\
  -d '{
    "cy_ref": "CY-AR8RWFDR",
    "checkout_id": "cart_abc",
    "reason": "declined",
    "currency": "CAD"
  }'`}</Code>
          </Section>

          <Section id="conversion" title="Report the conversion" subtitle="POST /api/v1/conversions — the only endpoint that records a verified conversion.">
            <p>Call this from your <b>server-side</b> post-payment handler (Stripe webhook, PayPal IPN, order-paid hook…). Requires a unique <code className="font-mono text-cyan-700">Idempotency-Key</code>. Replays return the original conversion — never a duplicate.</p>
            <Code lang="curl" testid="docs-conversion-curl">{`curl -X POST $CY/api/v1/conversions \\
  -H "Authorization: Bearer $KEY" \\
  -H "Idempotency-Key: order_ORD-10042" \\
  -H "Content-Type: application/json" \\
  -d '{
    "cy_ref": "CY-AR8RWFDR",
    "order_id": "ORD-10042",
    "currency": "CAD",
    "items": [{ "sku": "RUN-SHOE-123", "quantity": 1, "unit_price": 120.00 }],
    "subtotal": 120.00,
    "discount": 24.00,
    "tax": 0.00,
    "shipping": 0.00,
    "total": 96.00,
    "status": "paid",
    "purchased_at": "2026-02-14T18:20:33Z"
  }'`}</Code>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-900">
              <b>Trust boundary</b>: only this endpoint drives cashback, performance fees and verified conversion counts. The browser SDK cannot call it — the key would leak.
            </div>
          </Section>

          <Section id="refund" title="Refunds & cancellations" subtitle="PATCH /api/v1/conversions/&#123;id&#125; — for reversals, disputes, partial refunds.">
            <Code lang="curl">{`curl -X PATCH $CY/api/v1/conversions/conv_abc \\
  -H "Authorization: Bearer $KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "status": "REFUNDED", "reason": "Customer request" }'`}</Code>
            <p>State machine: <code className="font-mono">PENDING → CONFIRMED → PAID</code>, terminal <code className="font-mono">REFUNDED / CANCELLED</code>, plus <code className="font-mono">PARTIALLY_REFUNDED</code> and <code className="font-mono">DISPUTED</code>.</p>
          </Section>

          <Section id="webhooks" title="Webhooks">
            <p>Every event in your workflow can be relayed to your endpoint with signed HMAC payloads and automatic retries. Configure in <a href="/merchant/webhooks" className="text-cyan-700 underline">/merchant/webhooks</a>.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><code className="font-mono text-cyan-700">referral.created</code>, <code className="font-mono text-cyan-700">referral.clicked</code></li>
              <li><code className="font-mono text-cyan-700">checkout.started</code>, <code className="font-mono text-cyan-700">payment.attempted</code>, <code className="font-mono text-cyan-700">payment.failed</code></li>
              <li><code className="font-mono text-cyan-700">conversion.received</code>, <code className="font-mono text-cyan-700">conversion.voided</code>, <code className="font-mono text-cyan-700">order.refunded</code></li>
            </ul>
          </Section>

          {/* --- COUPON CODE INTEGRATION -------------------------------- */}

          <Section id="legacy" title="Coupon Code Integration" subtitle="Cyannect assigns a unique code to the shopper. Your checkout validates the code with Cyannect and applies the discount. Best for merchants who want to launch offers quickly with minimal engineering." badge="Coupon Code">
            <p>When the merchant picks <strong>Coupon Code</strong> during offer creation, the Cyannect app reveals a code (e.g. <code className="font-mono text-amber-700">CYN-AR8RWFDR</code>) to the shopper with a <em>Copy</em> button and a <em>Shop now</em> CTA. The shopper opens your merchant page, pastes the code at checkout, and your checkout validates it server-side against Cyannect.</p>
            <p className="text-slate-600 text-xs mt-1">This is a fully supported first-class product, not a legacy path. Merchants can freely choose <strong>Automatic Discount</strong> or <strong>Coupon Code</strong> per offer.</p>
            <div className="mt-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">1 · Validate the code at checkout</div>
            <Code lang="curl" testid="docs-legacy-validate-curl">{`curl -X POST $CY/api/v1/coupons/validate \\
  -H "X-Api-Key: $KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "coupon": "CYN-AR8RWFDR",
    "cart_value": 100.00,
    "currency": "CAD",
    "product_ids": ["sku_101","sku_202"]
  }'`}</Code>
            <div className="mt-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">2 · Report the successful redemption</div>
            <Code lang="curl" testid="docs-legacy-redeem-curl">{`curl -X POST $CY/api/v1/coupons/redeem \\
  -H "X-Api-Key: $KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "coupon": "CYN-AR8RWFDR",
    "order_id": "ORD-10042",
    "revenue": 100.00,
    "discount": 15.00,
    "currency": "CAD",
    "products": ["sku_101","sku_202"],
    "customer_id": "your_customer_id_abc"
  }'`}</Code>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 mt-3">
              <b>Financial parity</b>: successful <code className="font-mono">/coupons/redeem</code> calls feed the same verified-conversion, cashback and merchant-analytics pipeline as Automatic Discount conversions. Refunds are reported via the same <code className="font-mono">PATCH /api/v1/conversions/&#123;id&#125;</code> endpoint.
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
